export type LessonCalendarSortBy =
  | 'name'
  | 'type'
  | 'price'
  | 'day'
  | 'time'
  | 'enrollment'
  | 'term';

export type LessonCalendarSortOrder = 'asc' | 'desc';

export interface LessonCalendarFilterSnapshot {
  year: string;
  term: string;
  lessonType: string;
  skillLevel: string;
  ageGroup: string;
  location: string;
  court: string;
  sport: string;
  activeStatus: string;
  coachIds: string[];
  studentIds: string[];
  sortBy: LessonCalendarSortBy;
  sortOrder: LessonCalendarSortOrder;
}

const SORT_BY_VALUES: LessonCalendarSortBy[] = [
  'name',
  'type',
  'price',
  'day',
  'time',
  'enrollment',
  'term',
];

export function lessonCalendarFilterStorageKey(academyId: string): string {
  return `courtsync.calendar.lessonFilters.${academyId}`;
}

export function defaultLessonCalendarFilterSnapshot(
  defaultYear: string
): LessonCalendarFilterSnapshot {
  return {
    year: defaultYear,
    term: 'all',
    lessonType: 'all',
    skillLevel: 'all',
    ageGroup: 'all',
    location: 'all',
    court: 'all',
    sport: 'all',
    activeStatus: 'active',
    coachIds: [],
    studentIds: [],
    sortBy: 'name',
    sortOrder: 'asc',
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

export function parseLessonCalendarFilterSnapshot(
  raw: unknown,
  defaultYear: string
): LessonCalendarFilterSnapshot | null {
  if (!isRecord(raw)) return null;

  const defaults = defaultLessonCalendarFilterSnapshot(defaultYear);
  const sortBy = SORT_BY_VALUES.includes(raw.sortBy as LessonCalendarSortBy)
    ? (raw.sortBy as LessonCalendarSortBy)
    : defaults.sortBy;
  const sortOrder =
    raw.sortOrder === 'desc' || raw.sortOrder === 'asc' ? raw.sortOrder : defaults.sortOrder;

  return {
    year: typeof raw.year === 'string' ? raw.year : defaults.year,
    term: typeof raw.term === 'string' ? raw.term : defaults.term,
    lessonType: typeof raw.lessonType === 'string' ? raw.lessonType : defaults.lessonType,
    skillLevel: typeof raw.skillLevel === 'string' ? raw.skillLevel : defaults.skillLevel,
    ageGroup: typeof raw.ageGroup === 'string' ? raw.ageGroup : defaults.ageGroup,
    location: typeof raw.location === 'string' ? raw.location : defaults.location,
    court: typeof raw.court === 'string' ? raw.court : defaults.court,
    sport: typeof raw.sport === 'string' ? raw.sport : defaults.sport,
    activeStatus: typeof raw.activeStatus === 'string' ? raw.activeStatus : defaults.activeStatus,
    coachIds: readStringArray(raw.coachIds),
    studentIds: readStringArray(raw.studentIds),
    sortBy,
    sortOrder,
  };
}

export function readLessonCalendarFilterSnapshot(
  academyId: string,
  defaultYear: string
): LessonCalendarFilterSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lessonCalendarFilterStorageKey(academyId));
    if (!raw) return null;
    return parseLessonCalendarFilterSnapshot(JSON.parse(raw), defaultYear);
  } catch {
    return null;
  }
}

export function writeLessonCalendarFilterSnapshot(
  academyId: string,
  snapshot: LessonCalendarFilterSnapshot
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(lessonCalendarFilterStorageKey(academyId), JSON.stringify(snapshot));
  } catch {
    // ignore quota errors
  }
}

export function clearLessonCalendarFilterSnapshot(academyId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(lessonCalendarFilterStorageKey(academyId));
  } catch {
    // ignore
  }
}
